import { css } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useSalesLiveCoachingConversationsQuery } from '../../../queries/AIConversation/useSalesLiveCoachingConversationsQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { UI_VERSION_FOCUSED } from '../../focused-view/useFocusedViewEnabled';
import { formatCallDuration, formatCallStatus, formatUiVersion, isCallAnswered } from './callDisplay';
import { getCreatorLabel } from './creatorLabel';
import { ALL_CREATORS, type ICreatorOption, ListFilters } from './ListFilters';
import { styleSheet } from './styles';

interface IProps {
	selectedConversationId: string | null;
	onSelect: (id: string) => void;
}

/**
 * Formats a conversation's `creationDate` ISO string using the same `date-fns` helper the
 * `AIConversations` reference page (`pages/AIConversations/index.tsx`) uses, so the Coffee
 * review page renders timestamps consistently. Falls back to a placeholder for missing or
 * unparseable values.
 */
const formatConversationTimestamp = (value?: string): string => {
	if (!value) {
		return 'Unknown start time';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return 'Unknown start time';
	}

	return format(date, "MMM d, yyyy 'at' h:mm a");
};

/**
 * Left-panel list of an account's `SalesLiveCoaching` AI conversations, sorted newest-first.
 * Selecting a row invokes `onSelect` with the conversation id; the selected row is highlighted.
 *
 * Loads one page (max 25) at a time from the cursor-paginated query and appends the next page when
 * the bottom `Waypoint` scrolls into view (auto infinite-scroll), with a "Load more" button as a
 * manual fallback. The search / rep / date-range filters run client-side over the pages loaded so
 * far (the server filter only scopes by conversation type), so loading more may surface additional
 * matches that are not yet loaded.
 *
 * Infinite-scroll loop safety: the `Waypoint` is rendered whenever `hasNextPage` is true (NOT gated
 * on `isFetchingNextPage`), so it stays mounted across fetches and never remounts mid-pagination —
 * `react-waypoint` only fires `onEnter` on the transition into view, so it can't auto-fire in a loop
 * even if a page of rows doesn't overflow the scroll container. The `onEnter` handler also re-checks
 * `hasNextPage && !isFetchingNextPage` before fetching as belt-and-suspenders.
 *
 * Loading uses the shared `LoadingSpinner`; query errors surface via the hook's `meta` toast, so we
 * render an empty body rather than a broken list when data is unavailable. The `filter/account`
 * route is account-admin only, so a non-admin caller gets a 403 — we show a dedicated message.
 */
export function ConversationList({ selectedConversationId, onSelect }: IProps) {
	const { data, isLoading, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
		useSalesLiveCoachingConversationsQuery();

	// Conversations loaded so far (flattened across pages by the hook's `select`).
	const conversations = data?.conversations;
	// Per-conversation call view (conversation + its joined phone call), keyed by conversation id.
	const callViewsByConversationId = data?.callViewsByConversationId;
	// Total matching across ALL pages on the server (null until the first page resolves).
	const totalCount = data?.totalCount ?? null;

	// Client-side filter state. The server query is fixed to SalesLiveCoaching and account-wide, so
	// search / rep / date filtering happens over the already-loaded pages — no extra server params.
	const [search, setSearch] = React.useState('');
	const [creatorId, setCreatorId] = React.useState<string>(ALL_CREATORS);
	// Inclusive date bounds as ISO `YYYY-MM-DD` strings ('' = unbounded) — the shape `DateInput` uses.
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');

	const hasActiveFilters = search.trim() !== '' || creatorId !== ALL_CREATORS || startDate !== '' || endDate !== '';

	// Distinct creators derived from the loaded data so the dropdown options are stable
	// regardless of the other active filters.
	const creatorOptions = React.useMemo<ICreatorOption[]>(() => {
		const byId = new Map<string, ICreatorOption>();
		for (const conversation of conversations ?? []) {
			const id = conversation.creator?.id;
			if (id && !byId.has(id)) {
				byId.set(id, { id, label: getCreatorLabel(conversation.creator) });
			}
		}
		return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
	}, [conversations]);

	const filteredAndSortedConversations = React.useMemo(() => {
		if (!conversations) {
			return [];
		}

		// Normalize the date bounds to epoch millis once. End bound is widened to the end of the
		// selected day so the range is inclusive of conversations created later that day.
		const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
		const endMs = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null;
		const normalizedSearch = search.trim().toLowerCase();

		const filtered = conversations.filter(conversation => {
			// Creator filter: keep all when the sentinel is selected, else match on creator id.
			if (creatorId !== ALL_CREATORS && conversation.creator?.id !== creatorId) {
				return false;
			}

			// Date-range filter: keep rows whose creationDate is within [start, end] inclusive,
			// ignoring a bound when it is null. Conversations with no/unparseable date are excluded
			// only when a bound is active.
			if (startMs !== null || endMs !== null) {
				const time = conversation.creationDate ? new Date(conversation.creationDate).getTime() : Number.NaN;
				if (Number.isNaN(time)) {
					return false;
				}
				if (startMs !== null && time < startMs) {
					return false;
				}
				if (endMs !== null && time > endMs) {
					return false;
				}
			}

			// Search filter: case-insensitive substring over the row's title AND creator label only.
			// NOTE: message bodies are NOT loaded by the list query, so search cannot match message
			// text — it is intentionally limited to the fields rendered in each row.
			if (normalizedSearch) {
				const title = conversation.title?.trim() ?? '';
				const creatorLabel = getCreatorLabel(conversation.creator);
				const haystack = `${title} ${creatorLabel}`.toLowerCase();
				if (!haystack.includes(normalizedSearch)) {
					return false;
				}
			}

			return true;
		});

		return filtered.sort((a, b) => {
			const aTime = a.creationDate ? new Date(a.creationDate).getTime() : 0;
			const bTime = b.creationDate ? new Date(b.creationDate).getTime() : 0;
			return bTime - aTime;
		});
	}, [conversations, creatorId, startDate, endDate, search]);

	if (isLoading) {
		return (
			<div className={css(styleSheet.listBody)}>
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	// `filter/account` is account-admin only; the `/ai-coaching-review` route is admin-gated, but
	// surface a stricter server-side 403 clearly rather than as a confusing empty list.
	const isForbidden = (error as { systemCode?: number } | null | undefined)?.systemCode === 403;
	if (isForbidden) {
		return (
			<div className={css(styleSheet.listState)}>
				You don&rsquo;t have permission to view account-wide coaching conversations.
			</div>
		);
	}

	// Other errors are surfaced by the query's `meta.toastErrorMessage`; avoid rendering a broken
	// list. With no data at all (account has no coaching conversations), show the bare empty state —
	// the filter controls would be meaningless without any rows to filter.
	if (isError || (conversations?.length ?? 0) === 0) {
		return <div className={css(styleSheet.listState)}>No coaching conversations for this account</div>;
	}

	const loadedCount = conversations?.length ?? 0;
	const countLabel = hasActiveFilters
		? `${filteredAndSortedConversations.length} of ${loadedCount} loaded shown`
		: totalCount != null
			? `Showing ${loadedCount} of ${totalCount}`
			: `Showing ${loadedCount}`;

	return (
		<>
			<ListFilters
				search={search}
				onSearchChange={setSearch}
				creatorId={creatorId}
				onCreatorChange={setCreatorId}
				startDate={startDate}
				onStartDateChange={setStartDate}
				endDate={endDate}
				onEndDateChange={setEndDate}
				creatorOptions={creatorOptions}
			/>
			<div className={css(styleSheet.listBody)}>
				{filteredAndSortedConversations.length === 0 ? (
					<div className={css(styleSheet.listState)}>No conversations match your filters</div>
				) : (
					filteredAndSortedConversations.map(conversation => {
						const isSelected = conversation.id === selectedConversationId;
						// The phone call is joined onto each conversation by the list query, so scanning info
						// (outcome + which UI the rep was on) is available per row without an extra request.
						const phoneCall = callViewsByConversationId?.get(conversation.id)?.phoneCall;
						const answered = isCallAnswered(phoneCall);
						// Connected talk time (m:ss), shown right-aligned so longer calls are easy to scan for.
						const durationLabel = formatCallDuration(phoneCall?.duration);

						// Rows lead with the rep name + date. We intentionally do NOT render the conversation
						// `title`: for SalesLiveCoaching it is the rep's full opening prompt, which already shows
						// in the transcript on the right — duplicating it here made every row a wall of identical
						// boilerplate. (Search still matches the underlying title; see the filter above.)
						return (
							<Button
								key={conversation.id}
								variant='ghost'
								onClick={() => onSelect(conversation.id)}
								styles={[styleSheet.listItem, ...(isSelected ? [styleSheet.listItemSelected] : [])]}
							>
								<div className={css(styleSheet.listItemTitle)}>{getCreatorLabel(conversation.creator)}</div>
								<div className={css(styleSheet.listItemMeta)}>
									<span>{formatConversationTimestamp(conversation.creationDate)}</span>
									{durationLabel ? (
										<span className={css(styleSheet.listItemDuration)} title='Connected talk time'>
											{durationLabel}
										</span>
									) : null}
								</div>
								{phoneCall ? (
									<div className={css(styleSheet.listItemTags)}>
										{/* Outcome: a connected call shows its status (green); an unconnected one reads "No answer" (gray). */}
										<Pill
											variant='tag'
											size='small'
											color={answered ? 'green' : 'gray'}
											label={answered ? formatCallStatus(phoneCall.callStatus) : 'No answer'}
										/>
										{phoneCall.uiVersion ? (
											<Pill
												variant='tag'
												size='small'
												color={phoneCall.uiVersion === UI_VERSION_FOCUSED ? 'blue' : 'gray'}
												label={formatUiVersion(phoneCall.uiVersion)}
											/>
										) : null}
									</div>
								) : null}
							</Button>
						);
					})
				)}

				{/* Auto-load the next page when this sentinel scrolls into view. Kept mounted while
				    `hasNextPage` (independent of `isFetchingNextPage`) so it never remounts mid-pagination
				    and cannot auto-fire in a loop — see the loop-safety note in the component doc. */}
				{hasNextPage ? (
					<Waypoint
						key='coaching-conversations-waypoint'
						bottomOffset='-200px'
						onEnter={() => {
							if (hasNextPage && !isFetchingNextPage) {
								fetchNextPage();
							}
						}}
					/>
				) : null}

				<div className={css(styleSheet.listFooter)}>
					{hasNextPage ? (
						<Button
							variant='secondary'
							onClick={() => fetchNextPage()}
							disabled={isFetchingNextPage}
							styles={[styleSheet.loadMoreButton]}
						>
							{isFetchingNextPage ? 'Loading…' : 'Load more'}
						</Button>
					) : null}
					<span className={css(styleSheet.listCount)}>{countLabel}</span>
				</div>
			</div>
		</>
	);
}
